import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout, getCurrentUser } from '../services/authService';
import GlobalTimeTracker from './GlobalTimeTracker';
import { 
  FiHome, 
  FiCheckSquare, 
  FiUsers, 
  FiBriefcase, 
  FiSettings, 
  FiLogOut,
  FiMenu,
  FiX,
  FiBell,
  FiFolder,
  FiClock,
  FiFileText,
  FiCalendar,
  FiBarChart2,
  FiList
} from 'react-icons/fi';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const currentUser = getCurrentUser();
    setUser(currentUser);
  }, []);
  
  const role = user?.role || 'employee';
  
  // Define navigation based on user role
  const navigation = [
    { name: 'Dashboard', href: `/${role}/dashboard`, icon: FiHome },
    { name: 'Projects', href: `/${role}/projects`, icon: FiFolder },
    ...(role === 'admin' ? [{ name: 'Project Overview', href: '/admin/project-overview', icon: FiList }] : []),
    ...(role !== 'admin' ? [{ name: 'Tasks', href: `/${role}/tasks`, icon: FiCheckSquare }] : []),
    ...(role === 'admin' ? [{ name: 'Office Policies', href: '/admin/policies', icon: FiFileText }] : []),
    ...(role === 'admin' ? [{ name: 'Leave Requests', href: '/admin/leave-requests', icon: FiCalendar }] : []),
    ...(role === 'employee' ? [{ name: 'Timesheet', href: '/employee/timesheet', icon: FiClock }] : []),
    ...(role === 'employee' ? [{ name: 'Office Policy', href: '/employee/policy', icon: FiFileText }] : []),
    ...(role === 'employee' ? [] : [{ name: 'Team', href: role === 'admin' ? '/admin/team' : '/manager/team', icon: FiUsers }]),
    ...(role === 'admin' ? [{ name: 'Clients', href: '/admin/clients', icon: FiBriefcase }] : []),
    { name: 'Settings', href: '/settings', icon: FiSettings },
  ];
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };
  
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  return (
    <div className="h-screen flex overflow-hidden bg-secondary-50">
      <GlobalTimeTracker />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-secondary-900 bg-opacity-50 md:hidden"
          onClick={toggleSidebar}
        ></div>
      )}
      
      {/* Sidebar */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 pt-4 border-b border-secondary-200">
            <Link to="/dashboard" className="flex items-center">
              <span className="text-xl font-semibold text-primary-600">TaskFlowPro</span>
            </Link>
            <button 
              className="md:hidden text-secondary-500 hover:text-secondary-700"
              onClick={toggleSidebar}
            >
              <FiX size={24} />
            </button>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium ${
                    active
                      ? 'bg-primary-50 text-primary-700'
                      : 'text-secondary-700 hover:bg-secondary-100'
                  }`}
                >
                  <item.icon 
                    className={`mr-3 h-5 w-5 ${
                      active ? 'text-primary-500' : 'text-secondary-500'
                    }`} 
                  />
                  {item.name}
                </Link>
              );
            })}
          </nav>
          
          {/* User profile */}
          <div className="p-4 border-t border-secondary-200">
            <div className="flex items-center">
              <Link to="/profile" className="flex items-center flex-1 hover:bg-secondary-100 rounded p-2 -m-2">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-primary-200 flex items-center justify-center text-primary-700 font-medium">
                    {user?.name?.split(' ').map(n => n[0]).join('') || 'U'}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-secondary-900">{user?.name || 'User'}</p>
                  <p className="text-xs text-secondary-500">{user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1) || 'User'}</p>
                </div>
              </Link>
              <button 
                onClick={handleLogout}
                className="ml-2 text-secondary-500 hover:text-secondary-700"
                title="Logout"
              >
                <FiLogOut />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex flex-col flex-1 overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm z-10">
          <div className="h-16 flex items-center justify-between px-4 md:px-6">
            <button
              className="text-secondary-500 hover:text-secondary-700 md:hidden"
              onClick={toggleSidebar}
            >
              <FiMenu size={24} />
            </button>
            
            <div className="flex items-center ml-auto">
              <button className="p-2 text-secondary-500 hover:text-secondary-700 relative">
                <FiBell size={20} />
                <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-danger"></span>
              </button>
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;